import type { ReactNode } from 'react';
import { LoadFailed } from '../ui/LoadState';
import { Skeleton } from '../../ui/Skeleton';
import { fadeWhileLoading } from '../ui/styles';
import { IntegrationEmpty } from './IntegrationEmpty';
import type { Report } from './useReport';
import type { IntegrationId } from '../../core/types/insights';
type Props<T> = {
  report: Report<T>;
  /** "De klantcijfers" → "De klantcijfers konden niet laden". */
  what: string;
  /** Externe bron: een antwoord zonder data betekent "niet gekoppeld" (404 `not_connected`). */
  integration?: IntegrationId;
  children: (data: NonNullable<T>) => ReactNode;
};

/**
 * Laden, fout, niet gekoppeld of het rapport. Bij een andere periode blijven de oude cijfers
 * iets lichter staan tot de nieuwe er zijn (geen lege flits).
 */
export function ReportView<T>({
  report,
  what,
  integration,
  children
}: Props<T>) {
  const {
    data,
    status,
    reload
  } = report;
  if (status === 'error' && data === null) {
    return <LoadFailed title={`${what} konden niet laden`} onRetry={reload} data-id="element-1978" data-mp="2zis2mvqzb8b2" />;
  }
  if (integration && status === 'ready' && data === null) return <IntegrationEmpty id={integration} data-id="element-1979" data-mp="235ls4mkkbno9" />;
  if (data === null || data === undefined) {
    return <div role="status" aria-label={`${what} laden`} className="space-y-6" data-id="element-1980" data-mp="1o8uzcw5e9a7m">
        <Skeleton className="h-28 w-full rounded-lg" data-id="element-1981" data-mp="1oaz7jqabsvdp" />
        <Skeleton className="h-56 w-full rounded-lg" data-id="element-1982" data-mp="1t6eedy9x1r0m" />
      </div>;
  }
  return <div className={`space-y-6 ${fadeWhileLoading(status === 'loading')}`} aria-busy={status === 'loading'} data-id="element-1983" data-mp="1ff9o3msfub2y">
      {children(data as NonNullable<T>)}
    </div>;
}